/*
  Product Card JavaScript - handles AJAX add to cart functionality
  Supports both the full form button and the quick-add icon button.
*/

(function() {
  'use strict';

  // Cart update utilities
  function updateCartIconBubble(itemCount) {
    const cartIcons = document.querySelectorAll('[id^="cart-icon-bubble"]');
    cartIcons.forEach(cartIcon => {
      // Look for any existing count badge (supports multiple header variants)
      let countBadge = cartIcon.querySelector('sup[class*="cart-count"]');
      if (itemCount > 0) {
        if (!countBadge) {
          countBadge = document.createElement('sup');
          countBadge.className = 'menu-single-row__cart-count';
          const icon = cartIcon.querySelector('[data-lucide], svg');
          if (icon) {
            cartIcon.insertBefore(countBadge, icon);
          } else {
            cartIcon.insertBefore(countBadge, cartIcon.firstChild);
          }
        }
        countBadge.textContent = itemCount;
      } else if (countBadge) {
        countBadge.remove();
      }
    });
  }

  function updateCartCountBadges(itemCount) {
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(el => {
      el.textContent = itemCount;
    });
  }

  async function refreshCartCount(skipIconBubble) {
    try {
      const cartResponse = await fetch('/cart.js', {
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!cartResponse.ok) {
        throw new Error('Failed to fetch cart data');
      }

      const cartData = await cartResponse.json();
      const itemCount = cartData?.item_count || 0;
      if (!skipIconBubble) updateCartIconBubble(itemCount);
      updateCartCountBadges(itemCount);
      return cartData;
    } catch (error) {
      console.error('Error refreshing cart count:', error);
      return null;
    }
  }

  // Cart toast notification
  let toastEl = null;
  let toastTimer = null;
  let progressBar = null;

  function showCartToast() {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'cart-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      toastEl.innerHTML =
        '<div class="cart-toast__body">' +
          '<span class="cart-toast__check">' +
            '<svg width="14" height="14" viewBox="0 0 14 14" fill="none">' +
              '<path class="cart-toast__check-path" d="M3 7.5l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
          '</span>' +
          '<span class="cart-toast__text">Added to cart</span>' +
          '<a class="cart-toast__link" href="/cart">View cart \u2192</a>' +
          '<button class="cart-toast__dismiss" aria-label="Dismiss">' +
            '<svg width="14" height="14" viewBox="0 0 14 14" fill="none">' +
              '<path d="M4 4l6 6M10 4l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
            '</svg>' +
          '</button>' +
        '</div>' +
        '<div class="cart-toast__progress"><div class="cart-toast__progress-bar"></div></div>';
      document.body.appendChild(toastEl);

      progressBar = toastEl.querySelector('.cart-toast__progress-bar');

      toastEl.querySelector('.cart-toast__dismiss').addEventListener('click', dismissToast);
    }

    // Reset animation
    toastEl.classList.remove('cart-toast--visible', 'cart-toast--hiding');
    progressBar.style.animation = 'none';
    void toastEl.offsetWidth;
    toastEl.classList.add('cart-toast--visible');
    progressBar.style.animation = '';

    clearTimeout(toastTimer);
    toastTimer = setTimeout(dismissToast, 3200);
  }

  function dismissToast() {
    if (!toastEl) return;
    clearTimeout(toastTimer);
    toastEl.classList.add('cart-toast--hiding');
    toastEl.addEventListener('animationend', () => {
      toastEl.classList.remove('cart-toast--visible', 'cart-toast--hiding');
    }, { once: true });
  }

  // Shared add-to-cart logic
  async function addToCart(variantId, quantity) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: quantity
        }],
        sections: 'cart-icon-bubble',
        sections_url: window.location.pathname
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add to cart');
    }

    const responseData = await response.json();

    // Update cart icon bubble if section was returned
    let sectionUpdated = false;
    if (responseData.sections && responseData.sections['cart-icon-bubble']) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        const parsed = new DOMParser().parseFromString(responseData.sections['cart-icon-bubble'], 'text/html');
        cartIconBubble.replaceChildren(...parsed.body.childNodes);
        sectionUpdated = true;
      }
    }

    // Refresh cart count (skip icon bubble update if section already handled it)
    const cartData = await refreshCartCount(sectionUpdated);

    // Publish cart update event
    if (window.publish && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-card',
        productVariantId: variantId,
        cartData: cartData || responseData
      });
    } else {
      document.dispatchEvent(new CustomEvent('cart-update', {
        detail: {
          source: 'product-card',
          productVariantId: variantId,
          cartData: cartData || responseData
        }
      }));
    }

    return responseData;
  }

  // Handle product card form submissions
  function initProductCardForms() {
    const productCardForms = document.querySelectorAll('.product-card__form:not([data-ajax-initialized])');

    productCardForms.forEach(form => {
      form.setAttribute('data-ajax-initialized', 'true');

      form.addEventListener('submit', async function(e) {
        e.preventDefault();

        const form = e.target;
        const button = form.querySelector('.product-card__button');
        const variantIdInput = form.querySelector('input[name="id"]');
        const quantityInput = form.querySelector('input[name="quantity"]');

        if (!button || !variantIdInput || button.disabled) {
          return;
        }

        const variantId = variantIdInput.value;
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
        const buttonText = button.querySelector('.product-card__button-text');
        const buttonLoading = button.querySelector('.product-card__button-loading');
        const originalButtonText = buttonText ? buttonText.textContent.trim() : button.textContent.trim();

        // Disable button and show loading state
        button.disabled = true;
        button.classList.add('loading');

        if (buttonText) {
          buttonText.hidden = true;
          buttonText.style.display = 'none';
        }
        if (buttonLoading) {
          buttonLoading.hidden = false;
          buttonLoading.style.display = 'flex';
        } else {
          button.textContent = button.dataset.addingText || 'Adding to cart...';
        }

        try {
          await addToCart(variantId, quantity);
          showCartToast();

          // Show success message
          const addedText = button.dataset.addedText || 'Added to cart!';
          button.classList.remove('loading');

          if (buttonLoading) {
            buttonLoading.hidden = true;
            buttonLoading.style.display = 'none';
          }
          if (buttonText) {
            buttonText.hidden = false;
            buttonText.style.display = 'inline-block';
            buttonText.textContent = addedText;
          } else {
            button.textContent = addedText;
          }

          // If inside a popup, close it after brief success feedback
          const popup = form.closest('[data-variant-popup]');
          if (popup) {
            setTimeout(() => {
              popup.setAttribute('aria-hidden', 'true');
              document.body.classList.remove('popup-open');
              if (buttonText) buttonText.textContent = originalButtonText;
              button.disabled = false;
            }, 1200);
          } else {
            setTimeout(() => {
              if (buttonText) {
                buttonText.textContent = originalButtonText;
              } else {
                button.textContent = originalButtonText;
              }
              button.disabled = false;
            }, 2000);
          }

        } catch (error) {
          console.error('Error adding to cart:', error);

          const errorText = button.dataset.errorText || 'Error adding to cart';
          button.classList.remove('loading');
          button.disabled = false;

          if (buttonLoading) {
            buttonLoading.hidden = true;
            buttonLoading.style.display = 'none';
          }
          if (buttonText) {
            buttonText.hidden = false;
            buttonText.style.display = 'inline-block';
            buttonText.textContent = errorText;
          } else {
            button.textContent = errorText;
          }

          setTimeout(() => {
            if (buttonText) {
              buttonText.textContent = originalButtonText;
            } else {
              button.textContent = originalButtonText;
            }
          }, 2000);
        }
      });
    });
  }

  // Handle variant popup open/close and variant selection
  function initVariantPopups() {
    const popupTriggers = document.querySelectorAll('[data-variant-popup-trigger]:not([data-popup-initialized])');

    popupTriggers.forEach(trigger => {
      trigger.setAttribute('data-popup-initialized', 'true');
      const card = trigger.closest('.product-card');
      if (!card) return;

      const popup = card.querySelector('[data-variant-popup]');
      if (!popup) return;

      const popupContent = popup.querySelector('.product-card__popup-content');
      const closeButtons = popup.querySelectorAll('[data-popup-close]');
      const variantIdInput = popup.querySelector('[data-popup-variant-id]');
      const priceDisplay = popup.querySelector('[data-popup-price]');
      const submitButton = popup.querySelector('.product-card__popup-submit');

      // Parse variants data
      let variants = [];
      const variantsScript = popup.querySelector('[data-popup-variants]');
      if (variantsScript) {
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch (e) {
          console.error('Error parsing variants:', e);
        }
      }

      function openPopup() {
        // Move popup to body so it's not clipped by card overflow
        if (popup.parentElement !== document.body) {
          document.body.appendChild(popup);
        }
        popup.setAttribute('aria-hidden', 'false');
        document.body.classList.add('popup-open');
        requestAnimationFrame(() => {
          const firstClose = popup.querySelector('[data-popup-close].product-card__popup-close');
          if (firstClose) firstClose.focus();
        });
      }

      function closePopup() {
        popup.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('popup-open');
        trigger.focus();
      }

      trigger.addEventListener('click', openPopup);

      closeButtons.forEach(btn => {
        btn.addEventListener('click', closePopup);
      });

      // Close on Escape
      popup.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closePopup();
      });

      // Prevent clicks inside content from closing
      popupContent.addEventListener('click', (e) => {
        e.stopPropagation();
      });

      // Handle option changes within the popup
      popup.querySelectorAll('.product-card__popup-input').forEach(input => {
        input.addEventListener('change', () => {
          // Update the displayed selected value
          const fieldset = input.closest('.product-card__popup-option');
          if (fieldset) {
            const valueDisplay = fieldset.querySelector('.product-card__popup-option-value');
            if (valueDisplay) valueDisplay.textContent = input.value;
          }

          // Find matching variant
          const selectedOptions = [];
          popup.querySelectorAll('.product-card__popup-option').forEach(fs => {
            const checked = fs.querySelector('.product-card__popup-input:checked');
            if (checked) selectedOptions.push(checked.value);
          });

          const matchedVariant = variants.find(v => {
            return v.options.every((opt, i) => opt === selectedOptions[i]);
          });

          if (matchedVariant) {
            // Update hidden variant ID
            if (variantIdInput) variantIdInput.value = matchedVariant.id;

            // Update price
            if (priceDisplay) {
              const formatted = window.Shopify && window.Shopify.formatMoney
                ? window.Shopify.formatMoney(matchedVariant.price)
                : '$' + (matchedVariant.price / 100).toFixed(2);
              priceDisplay.textContent = formatted;
            }

            // Update submit button availability
            if (submitButton) {
              const buttonText = submitButton.querySelector('.product-card__button-text');
              if (!buttonText) return;

              // Store the original add-to-cart text on first run
              if (!submitButton.dataset.originalButtonText) {
                submitButton.dataset.originalButtonText = buttonText.textContent.trim();
              }

              if (matchedVariant.available) {
                submitButton.disabled = false;
                buttonText.textContent = submitButton.dataset.originalButtonText;
              } else {
                submitButton.disabled = true;
                buttonText.textContent = submitButton.dataset.soldOutText || 'Sold out';
              }
            }

            // Update popup product image to variant image
            const popupImage = popup.querySelector('.product-card__popup-image img');
            if (popupImage && matchedVariant.featured_image && matchedVariant.featured_image.src) {
              popupImage.src = matchedVariant.featured_image.src.replace(/(\.[^.]+)$/, '_160x$1');
            }
          }
        });
      });
    });
  }

  function initAll() {
    initProductCardForms();
    initVariantPopups();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-initialize for dynamically added product cards
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      let needsInit = false;
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            if ((node.classList && (node.classList.contains('product-card__form') || node.classList.contains('product-card'))) ||
                (node.querySelector && (node.querySelector('.product-card__form') || node.querySelector('[data-variant-popup-trigger]')))) {
              needsInit = true;
            }
          }
        });
      });
      if (needsInit) initAll();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();
